import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { useChampionship } from "@/context/ChampionshipContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import ImageUpload from "@/components/ImageUpload";
import { Plus, Copy, Pencil, Trash2 } from "lucide-react";

const blank = {
  year: new Date().getFullYear() + 1, location: "", country: "", start_date: "", end_date: "",
  tagline: "", hero_image_path: "", intro_html: "", live_stream_url: "", live_standings_url: "",
};

export default function AdminChampionshipsPage() {
  const { all, reload, current } = useChampionship() || {};
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState("create"); // create | edit | clone
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(blank);

  const openNew = () => { setMode("create"); setEditing(null); setForm(blank); setOpen(true); };
  const openClone = (c) => {
    setMode("clone");
    setEditing(c);
    // Auto-shift dates by one year and clear host city/country so admin chooses
    const shift = (iso) => {
      if (!iso) return "";
      try { const d = new Date(iso); d.setFullYear(d.getFullYear() + 1); return d.toISOString().slice(0, 10); }
      catch { return ""; }
    };
    setForm({
      ...c,
      year: c.year + 1,
      location: "",
      country: "",
      start_date: shift(c.start_date),
      end_date: shift(c.end_date),
      id: undefined,
      is_current: undefined,
    });
    setOpen(true);
  };
  const openEdit = (c) => { setMode("edit"); setEditing(c); setForm({ ...c }); setOpen(true); };

  const save = async () => {
    try {
      if (mode === "edit") await api.patch(`/championships/${editing.id}`, form);
      else if (mode === "clone") await api.post(`/championships/${editing.id}/clone`, form);
      else await api.post("/championships", form);
      toast.success("Saved");
      setOpen(false);
      await reload();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const setCurrent = async (c) => {
    try { await api.patch(`/championships/${c.id}`, { is_current: true }); toast.success(`${c.year} set as current`); await reload(); }
    catch (err) { toast.error(formatApiError(err)); }
  };

  const remove = async (c) => {
    if (!confirm(`Delete WOC ${c.year}? This removes its news, info pages, sponsors and players.`)) return;
    try { await api.delete(`/championships/${c.id}`); toast.success("Deleted"); await reload(); }
    catch (err) { toast.error(formatApiError(err)); }
  };

  function dialogTitle() {
    if (mode === "edit") return `Edit WOC ${editing?.year}`;
    if (mode === "clone") return `Clone WOC ${editing?.year} → new edition`;
    return "New championship edition";
  }

  return (
    <div className="p-8 lg:p-10" data-testid="admin-championships">
      <div className="flex items-center justify-between mb-8">
        <div>
          <div className="text-[11px] uppercase tracking-[0.3em] text-muted-foreground">Championships</div>
          <h1 className="font-display text-3xl font-extrabold tracking-tight mt-2">Editions</h1>
          <p className="text-sm text-muted-foreground mt-1">Manage multi-year championships. Clone a previous edition to start a new year.</p>
        </div>
        <Button onClick={openNew} className="bg-primary text-primary-foreground hover:bg-primary/90" data-testid="champ-new-button">
          <Plus className="h-4 w-4 mr-2" /> New edition
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {(all || []).map((c) => (
          <div key={c.id} className="border border-border bg-white p-5" data-testid={`champ-card-${c.year}`}>
            <div className="flex items-start justify-between">
              <div>
                <div className="text-[10px] uppercase tracking-[0.25em] text-muted-foreground">Edition</div>
                <div className="font-display text-3xl font-extrabold tracking-tight">{c.year}</div>
                <div className="text-sm">{c.location}, {c.country}</div>
                <div className="text-xs text-muted-foreground mt-1">{c.start_date} → {c.end_date}</div>
              </div>
              {c.is_current && <Badge className="bg-primary text-primary-foreground">Current</Badge>}
            </div>
            <div className="flex gap-2 mt-4">
              {!c.is_current && (
                <Button size="sm" variant="outline" onClick={() => setCurrent(c)} data-testid={`set-current-${c.year}`}>Set current</Button>
              )}
              <Button size="sm" variant="ghost" onClick={() => openEdit(c)}><Pencil className="h-4 w-4 mr-1" /> Edit</Button>
              <Button size="sm" variant="ghost" onClick={() => openClone(c)} data-testid={`clone-${c.year}`}><Copy className="h-4 w-4 mr-1" /> Clone</Button>
              <Button size="sm" variant="ghost" onClick={() => remove(c)} className="text-destructive ml-auto"><Trash2 className="h-4 w-4" /></Button>
            </div>
          </div>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{dialogTitle()}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>Year</Label>
                <Input type="number" value={form.year} onChange={(e) => setForm({ ...form, year: parseInt(e.target.value) || 0 })} data-testid="champ-year-input" />
              </div>
              <div className="space-y-2">
                <Label>Host city</Label>
                <Input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} data-testid="champ-city-input" />
              </div>
              <div className="space-y-2">
                <Label>Country</Label>
                <Input value={form.country} onChange={(e) => setForm({ ...form, country: e.target.value })} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Start date</Label>
                <Input type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} />
              </div>
              <div className="space-y-2">
                <Label>End date</Label>
                <Input type="date" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Tagline</Label>
              <Input value={form.tagline || ""} onChange={(e) => setForm({ ...form, tagline: e.target.value })} />
            </div>
            <ImageUpload label="Hero image" value={form.hero_image_path} onChange={(v) => setForm({ ...form, hero_image_path: v })} />
            <div className="space-y-2">
              <Label>Intro HTML</Label>
              <Textarea rows={3} value={form.intro_html || ""} onChange={(e) => setForm({ ...form, intro_html: e.target.value })} />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Live stream URL</Label>
                <Input value={form.live_stream_url || ""} onChange={(e) => setForm({ ...form, live_stream_url: e.target.value })} />
              </div>
              <div className="space-y-2">
                <Label>Live standings URL</Label>
                <Input value={form.live_standings_url || ""} onChange={(e) => setForm({ ...form, live_standings_url: e.target.value })} />
              </div>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
              <Button onClick={save} className="bg-primary text-primary-foreground hover:bg-primary/90" data-testid="champ-save-button">Save</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
